import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { RedirectError } from '#src/shared/error';

import { differenceInDays } from '#src/app/shared/utils/dates';
import { isEligibleForTrafficPercentage } from '#src/app/shared/utils/featureRollout';

import { anonymizeForBots } from './anonymizeForBots';
import type { PetitionDetailsDataQuery, PetitionDetailsDataQueryVariables } from './api.graphql';
import { PetitionDetailsData } from './api.graphql';
import { getPetitionDiscussionData } from './discussionData';
import { getPetitionAdvocates } from './getPetitionAdvocates';
import type { AdvocatesForPetitionQuery } from './getPetitionAdvocates/petitionAdvocates.graphql';
import { getPetitionStrengthScore } from './getPetitionStrengthScore';
import { getPetitionRecentSignersData } from './recentSignersData';
import { getSupporterVoicesVideos } from './supporterVoicesVideos';
import type { PetitionDetailsDataQueryPetition, PetitionDetailsPageData } from './types';
import { getVideoFirstHeroData } from './videoFirstHero';
import type { VideoFirstHeroData } from './videoFirstHero';

async function fetchPetitionDetails(utilityContext: UtilityContext, variables: PetitionDetailsDataQueryVariables) {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<PetitionDetailsDataQuery, PetitionDetailsDataQueryVariables>({
		query: PetitionDetailsData,
		variables,
		rejectOnError: true,
		// to separate query from other batched queries
		batched: false,
	});

	return data;
}

function normalizePetition(petition: PetitionDetailsDataQueryPetition): PetitionDetailsPageData['petition'] {
	return {
		...petition,
		dmsWithLegislativeBodiesConnection: {
			pageInfo: { hasNextPage: petition.dmsWithLegislativeBodiesConnection.pageInfo.hasNextPage },
			totalCount: petition.dmsWithLegislativeBodiesConnection.totalCount,
			nodes: petition.dmsWithLegislativeBodiesConnection.nodes,
		},
	};
}

async function getVideoFirstHeroPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{ enabled, isBot }: { enabled: boolean; isBot: boolean },
): Promise<VideoFirstHeroData> {
	if (!enabled || isBot) return { enabled: false };
	return getVideoFirstHeroData(utilityContext, slugOrId);
}

function normalizeMediaHitsPage(
	mediaHitsConnection: PetitionDetailsDataQueryPetition['mediaHitsConnection'],
): PetitionDetailsPageData['mediaHitsPage'] {
	const { pageInfo, totalCount, totalCountLocalized, nodes } = mediaHitsConnection;

	return {
		isLastPage: !pageInfo.hasNextPage,
		numberOfMediaHits: totalCount,
		numberOfMediaHitsLocalized: totalCountLocalized,
		mediaHits: nodes,
		// Only expose a cursor when another page exists, so consumers can treat "has a cursor" as "has more".
		endCursor: pageInfo.hasNextPage ? (pageInfo.endCursor ?? null) : null,
	};
}

export async function getPetitionDetailsPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		locale,
		countryCode,
		isBot,
	}: {
		locale: string;
		countryCode: string;
		isBot: boolean;
	},
): Promise<PetitionDetailsPageData> {
	const [data, discussionData, recentSignersData] = await Promise.all([
		fetchPetitionDetails(utilityContext, {
			slugOrId,
			locale,
			countryCode,
		}),
		getPetitionDiscussionData(utilityContext, {
			slugOrId,
			commentsSortBy: 'SCORE',
		}),
		getPetitionRecentSignersData(utilityContext, { slugOrId }),
	]);

	if (!data.petition) throw new RedirectError({ url: '/?petition_not_found=true' });

	const [supporterVoicesVideos, videoFirstHero] = await Promise.all([
		getSupporterVoicesVideos(utilityContext, slugOrId, !data.petition.canDisplayVideo),
		getVideoFirstHeroPageData(utilityContext, slugOrId, {
			enabled: data.featureConfigs.videoFirstPetitionPage,
			isBot,
		}),
	]);

	const petitionWithSideData = { ...data.petition, ...discussionData, ...recentSignersData };

	const rawPetition = isBot ? anonymizeForBots(petitionWithSideData) : petitionWithSideData;

	const petition = normalizePetition(rawPetition);

	let petitionAdvocates: AdvocatesForPetitionQuery['advocatesForPetition'] = [];

	if (petition.isCommunityPetition) {
		const { advocatesForPetition } = await getPetitionAdvocates(utilityContext, {
			slugOrId: petition.id,
		});

		petitionAdvocates = advocatesForPetition;
	}

	const starterUserId = data.petition.user.id;

	return {
		petition,
		userSelectableCountries: data.userSelectableCountries,
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		createdLessThanOneDayAgo: differenceInDays(new Date(), new Date(data.petition.createdAt)) < 1,
		score: getPetitionStrengthScore(data.petition),
		supporterVoicesVideos,
		videoFirstHero,
		mediaHitsPage: normalizeMediaHitsPage(petition.mediaHitsConnection),
		mediaSourcesPage: {
			numberOfMediaSources: petition.mediaSourcesConnection.totalCount,
			mediaSources: petition.mediaSourcesConnection.nodes,
		},
		featureConfigs: data.featureConfigs,
		petitionAdvocates,
		iOSBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppIosMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		androidBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppAndroidMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		desktopBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppCombinedDesktopDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
	};
}
